import { Component, ChangeDetectionStrategy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractPageComponent, BasePageComponent } from '@spike/scaffolds';
import { AsyncApiService, ResourceApiService } from '@spike/core';
import { Trade } from '../../models/trade.model';
import { EntityApiEnum } from 'src/app/config/entity-api.enum';
import { TradeMonthlyRecapTableComponent } from "../../components/trade-monthly-recap-table/trade-monthly-recap-table";
import { MonthlyRecap } from '../../models/monthly-recap.model';
import { EtiquetteStoreService } from '../../services/etiquette-service/etiquette-store.service';
import { FinancialUtilityService } from '../../services/utility/financial-utility.service';
import { Button } from 'primeng/button'
import { TradesTableComponent } from "../../components/trades-table/trades-table";
import { Drawer } from "primeng/drawer";
@Component({
  selector: 'spike-trade-recap-page',
  templateUrl: './trade-recap-page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, BasePageComponent, TradeMonthlyRecapTableComponent, Button, TradesTableComponent, Drawer]
})
export class TradeRecapPageComponent extends AbstractPageComponent {
  private readonly resourceApi = inject(ResourceApiService);
  private readonly asyncApi = inject(AsyncApiService);
  public readonly etiquetteStoreService = inject(EtiquetteStoreService);

  public readonly breadcrumb = [{ label: 'Flussi di cassa' }];
  
  // Trasformiamo l'anno in un signal per renderlo reattivo
  public year = signal<number>(new Date().getFullYear());
  public isDetailVisible = signal<boolean>(false);
  public trades = signal<Trade[]>([]);
  
  private entityApi = EntityApiEnum.FIN_TRADE_ETIQUETTE_RECAP;
  public readonly tradeEtiquetteResource = this.resourceApi.selectAll<MonthlyRecap>(signal(this.entityApi));

  // Signal per le etichette
  private etiquetteList = computed(() => this.etiquetteStoreService.getList()());

  // COMPUTED PRINCIPALE: Elabora i dati filtrati per l'anno e arricchiti dalle etichette
  private yearlist = computed(() => {
    const rawList = this.tradeEtiquetteResource.value();
    const etiquettes = this.etiquetteList();
    const currentYear = this.year();

    if (!rawList.length) return [];

    // Filtriamo per l'anno corrente
    let filtered = rawList.filter(y => y.accountingYear === currentYear);

    // Integriamo le etichette mancanti (logica ex loadCurrentYearRecap)
    return etiquettes.map(etiquette => {
      const existing = filtered.find(u => u.etiquetteId === etiquette.etiquetteId);
      if (existing) {
        return { ...existing, shortDescription: etiquette.shortDescription };
      }
      return {
        etiquetteId: etiquette.etiquetteId,
        shortDescription: etiquette.shortDescription,
        accountingYear: currentYear,
        jan: 0, feb: 0, mar: 0, apr: 0, may: 0, jun: 0, 
        jul: 0, aug: 0, sep: 0, oct: 0, nov: 0, dic: 0, tot: 0
      } as MonthlyRecap;
    });
  });

  // COMPUTED DERIVATI: Si aggiornano automaticamente quando yearlist cambia
  public tradeEtiquetteRecapIncomeList = computed(() => 
    this.yearlist().filter(x => x.tot > 0).sort((a, b) => a.shortDescription.localeCompare(b.shortDescription))
  );

  public tradeEtiquetteRecapOutcomeList = computed(() => 
    this.yearlist().filter(x => x.tot < 0).sort((a, b) => a.shortDescription.localeCompare(b.shortDescription))
  );

  public tradeEtiquetteRecapTotalList = computed(() => {
    const list = this.yearlist();
    const total = FinancialUtilityService.getTotal(list);
    total.shortDescription = 'GRAND TOTAL';
    total.isBoldRow = true;
    return [total];
  });

  public tradeEtiquetteRecapSavingRateList = computed(() => {
    const income = this.tradeEtiquetteRecapIncomeList();
    const outcome = this.tradeEtiquetteRecapOutcomeList();
    return [this.buildSavingRate(income, outcome)];
  });

  // Metodi di navigazione semplificati
  pagePrev() {
    const allEntries = this.tradeEtiquetteResource.value();
    if (allEntries.some(x => x.accountingYear === this.year() - 1)) {
      this.year.update(y => y - 1);
    }
  }

  pageNext() {
    const allEntries = this.tradeEtiquetteResource.value();
    if (allEntries.some(x => x.accountingYear === this.year() + 1)) {
      this.year.update(y => y + 1);
    }
  }

  private buildSavingRate(incomeList: MonthlyRecap[], outcomeList: MonthlyRecap[]): MonthlyRecap {
    const savingRate = { shortDescription: 'SAVING RATE', isBoldRow: true } as MonthlyRecap;
    const totalIncome = FinancialUtilityService.getTotal(incomeList);
    const totalOutcome = FinancialUtilityService.getTotal(outcomeList);

    const months: (keyof MonthlyRecap)[] = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dic', 'tot'];
    
    months.forEach(m => {
      const inc = (totalIncome[m] as number) || 0;
      const out = (totalOutcome[m] as number) || 0;
      // Evitiamo divisione per zero
      (savingRate[m] as any) = inc !== 0 ? (1 + (out / inc)) * 100 : 0;
    });

    return savingRate;
  }

  public async openDetail(m: MonthlyRecap) {
    const tradeFilter: Trade = { 
        etiquetteId: m.etiquetteId, 
        accountingMonth: m.monthClicked 
    } as Trade;
    const trades = await this.asyncApi.search<Trade>(EntityApiEnum.FIN_TRADE, tradeFilter, { currentPage: 0, rows: 100 });
    this.trades.set(trades.list)

    this.isDetailVisible.set(true);
  }
}